import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  AcademicCapIcon,
  MagnifyingGlassIcon,
  DocumentMagnifyingGlassIcon,
  PencilSquareIcon,
  FunnelIcon,
  ClipboardDocumentCheckIcon,
  ShieldCheckIcon,
  IdentificationIcon,
  ChatBubbleLeftRightIcon,
  SparklesIcon,
  EyeIcon,
  ScaleIcon,
  ChartBarIcon,
  UserGroupIcon,
  TableCellsIcon,
  BookOpenIcon,
  CheckBadgeIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CalendarDaysIcon,
  EnvelopeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentTextIcon,
} from 'components/icons';

// =============================================================================
// How Clevor Works — five stages, each opening onto the features inside it
// =============================================================================
//
// This replaces the four static "From posted to submitted" cards. Those cards
// were accurate but carried one sentence each, which meant the compliance
// matrix, the past performance library, the reviewer agent and the readiness
// checklist were invisible to anyone who had not already signed up. Collapsing
// the old redundant sections fixed the repetition and left the features with
// nowhere to live; this is where they live.
//
// Every chip below names something that exists in the product today. The rule
// is the same one the differentiator rows follow: if a visitor cannot go find
// it after signing up, it does not go on this page.

type Chip = {
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

type Stage = {
  id: string;
  title: string;
  headline: string;
  text: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  // Held as whole class strings rather than composed from a colour name —
  // Tailwind's scanner only sees literals, and `bg-${c}-50` compiles to nothing.
  ring: string;
  tileBg: string;
  tileFg: string;
  railOn: string;
  railOff: string;
  glow: string;
  chips: Chip[];
  /** A capture of the real screen for this stage; shown in place of the chip orbit when present. */
  shot?: { src: string; alt: string };
};

const STAGES: Stage[] = [
  {
    id: 'learn',
    title: 'Learn',
    shot: { src: '/shots/learn.webp', alt: 'An opportunity page with the eligibility verdict: one fatal issue — an SDVOSB set-aside the profile cannot meet — and two warnings, each with what to fix.' },
    headline: 'Find out what would disqualify you — before you find a bid',
    text:
      'Most contractors discover a missing registration the week a proposal is due. Clevor works out what you are eligible for from your profile, and tells you what to fix first.',
    icon: AcademicCapIcon,
    ring: 'ring-sky-200',
    tileBg: 'bg-sky-50',
    tileFg: 'text-sky-600',
    railOn: 'bg-sky-600 text-white ring-sky-200',
    railOff: 'text-sky-600 bg-sky-50',
    glow: 'from-sky-100/70',
    chips: [
      { label: 'Bid readiness checklist', icon: ClipboardDocumentCheckIcon },
      { label: 'Set-aside eligibility', icon: ShieldCheckIcon },
      { label: 'NAICS alignment', icon: IdentificationIcon },
      { label: 'SAM registration gaps', icon: CheckBadgeIcon },
      { label: 'AI consultant', icon: ChatBubbleLeftRightIcon },
    ],
  },
  {
    id: 'find',
    title: 'Find',
    shot: { src: '/shots/matches.webp', alt: 'The Opportunities page on its New matches tab: three solicitations that fit the saved criteria, each with the reasons it matched and its deadline.' },
    headline: 'Federal solicitations, scored against your business',
    text:
      'Every open opportunity on SAM.gov, ranked by how well it matches your NAICS codes, your set-aside status, and the work you have actually done. Watch the ones worth watching.',
    icon: MagnifyingGlassIcon,
    ring: 'ring-blue-200',
    tileBg: 'bg-blue-50',
    tileFg: 'text-blue-600',
    railOn: 'bg-blue-600 text-white ring-blue-200',
    railOff: 'text-blue-600 bg-blue-50',
    glow: 'from-blue-100/70',
    chips: [
      { label: 'SAM.gov search', icon: MagnifyingGlassIcon },
      { label: 'Match scoring', icon: SparklesIcon },
      { label: 'Watch list', icon: EyeIcon },
      { label: 'Deadline tracking', icon: CalendarDaysIcon },
    ],
  },
  {
    id: 'analyze',
    title: 'Analyze',
    headline: 'Read the package, not the summary',
    text:
      'Requirements, evaluation factors and submission instructions pulled out of the actual documents — each answer citing the section it came from, so you can check it.',
    icon: DocumentMagnifyingGlassIcon,
    ring: 'ring-emerald-200',
    tileBg: 'bg-emerald-50',
    tileFg: 'text-emerald-600',
    railOn: 'bg-emerald-600 text-white ring-emerald-200',
    railOff: 'text-emerald-600 bg-emerald-50',
    glow: 'from-emerald-100/70',
    chips: [
      { label: 'Requirement extraction', icon: DocumentTextIcon },
      { label: 'Evaluation criteria', icon: ScaleIcon },
      { label: 'Compliance check', icon: ShieldCheckIcon },
      { label: 'Market analysis', icon: ChartBarIcon },
      { label: 'Teaming partners', icon: UserGroupIcon },
    ],
  },
  {
    id: 'draft',
    title: 'Draft',
    headline: 'The sections the solicitation asks for, in the order it asks',
    text:
      'Proposal volumes grounded in the extracted requirements and your own past performance, checked against a compliance matrix, reviewed, then exported as a document you can submit.',
    icon: PencilSquareIcon,
    ring: 'ring-purple-200',
    tileBg: 'bg-purple-50',
    tileFg: 'text-purple-600',
    railOn: 'bg-purple-600 text-white ring-purple-200',
    railOff: 'text-purple-600 bg-purple-50',
    glow: 'from-purple-100/70',
    chips: [
      { label: 'Proposal writer', icon: PencilSquareIcon },
      { label: 'Compliance matrix', icon: TableCellsIcon },
      { label: 'Past performance library', icon: BookOpenIcon },
      { label: 'Reviewer pass', icon: CheckBadgeIcon },
      { label: 'Word export', icon: ArrowDownTrayIcon },
    ],
  },
  {
    id: 'track',
    title: 'Track',
    shot: { src: '/shots/pipeline.webp', alt: 'The pipeline board: pursuits in New, Qualified and Proposal columns with the next action on each card.' },
    headline: 'One pipeline instead of a spreadsheet',
    text:
      'What you are watching, pursuing and submitting on one board — and a watch on every tracked solicitation, so an amendment or a moved deadline reaches you rather than the other way round.',
    icon: FunnelIcon,
    ring: 'ring-amber-200',
    tileBg: 'bg-amber-50',
    tileFg: 'text-amber-600',
    railOn: 'bg-amber-500 text-white ring-amber-200',
    railOff: 'text-amber-600 bg-amber-50',
    glow: 'from-amber-100/70',
    chips: [
      { label: 'Pipeline board', icon: FunnelIcon },
      { label: 'Amendment alerts', icon: ArrowPathIcon },
      { label: 'Deadline changes', icon: CalendarDaysIcon },
      { label: 'Email drafting', icon: EnvelopeIcon },
    ],
  },
];

// -----------------------------------------------------------------------------
// Orbit geometry
// -----------------------------------------------------------------------------
// Chips launch from behind the centre tile and settle onto an ellipse around
// it. The box is a fixed size so the launch offsets can be plain pixels — a
// percentage `x` in Framer Motion resolves against the element, not the
// container, which would send every chip a different distance depending on how
// long its label is. Fixed also means the layout cannot reflow mid-flight.

// Sized against the narrowest container that shows the orbit at all: at the lg
// breakpoint the panel has 880px of inner width, so 860 is the ceiling.
const ORBIT_W = 860;
const ORBIT_H = 440;
// Horizontal radius is the constrained one — a chip is ~200px wide and only
// ~38px tall, so the ellipse has to be far wider than it is tall before the
// chips at 54° stop clipping the corner of the centre tile.
const RADIUS_X = 0.38;
const RADIUS_Y = 0.40;

function orbitSlot(index: number, count: number) {
  // Start at twelve o'clock and go clockwise, so the first chip in the list is
  // the first one read.
  const angle = ((-90 + (360 / count) * index) * Math.PI) / 180;
  const left = 50 + RADIUS_X * 100 * Math.cos(angle);
  const top = 50 + RADIUS_Y * 100 * Math.sin(angle);
  return {
    left: `${left}%`,
    top: `${top}%`,
    // Distance back to the centre tile, where the chip starts.
    dx: (50 - left) * (ORBIT_W / 100),
    dy: (50 - top) * (ORBIT_H / 100),
  };
}

// -----------------------------------------------------------------------------

const ChipPill: React.FC<{ chip: Chip; stage: Stage; className?: string }> = ({
  chip,
  stage,
  className = '',
}) => (
  <div
    className={`flex items-center gap-2 rounded-xl bg-white px-3.5 py-2.5 shadow-sm ring-1 ${stage.ring} ${className}`}
  >
    <chip.icon className={`h-4 w-4 flex-shrink-0 ${stage.tileFg}`} aria-hidden="true" />
    <span className="whitespace-nowrap text-sm font-medium text-content">{chip.label}</span>
  </div>
);

const HowItWorks: React.FC = () => {
  const [active, setActive] = useState(0);
  const [userPicked, setUserPicked] = useState(false);
  const reduceMotion = useReducedMotion();

  const stage = STAGES[active];

  // Same contract as the hero showcase on this page: advance on its own until
  // the visitor takes control, then stop for good. Moving a panel out from
  // under someone who is reading it is worse than never moving at all.
  useEffect(() => {
    if (userPicked || reduceMotion) return;
    const id = setInterval(() => setActive((i) => (i + 1) % STAGES.length), 7000);
    return () => clearInterval(id);
  }, [userPicked, reduceMotion]);

  const pick = useCallback((i: number) => {
    setUserPicked(true);
    setActive(((i % STAGES.length) + STAGES.length) % STAGES.length);
  }, []);

  const slots = useMemo(
    () => stage.chips.map((_, i) => orbitSlot(i, stage.chips.length)),
    [stage],
  );

  return (
    <div id="how-it-works" className="scroll-mt-20 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 1, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mx-auto mb-14 max-w-2xl text-center"
        >
          <h2 className="text-title-lg text-balance text-content sm:text-display">
            {/* Same treatment as the wordmark in the header. The gradient is
                on the name only — the whole heading in gradient is a
                different, louder thing. */}
            How <span className="gradient-text">Clevor</span> works
          </h2>
          <p className="mt-6 text-lg leading-8 text-content-muted">
            Five stages between deciding to bid federal work and sending a proposal out the
            door. Pick one to see what Clevor does inside it.
          </p>
        </motion.div>

        {/* ---------------------------------------------------------------- */}
        {/* Stage rail                                                        */}
        {/* ---------------------------------------------------------------- */}
        {/* The rail keeps all five stages visible and does not resize when a
            stage opens — the panel below changes instead. A rail that expands
            in place pushes the stage you just clicked out from under the
            cursor. Horizontally scrollable on small screens rather than
            wrapped, so the sequence still reads as a sequence. */}
        <div
          className="mb-10 pt-3 pb-2 lg:pt-0"
          role="tablist"
          aria-label="How Clevor works"
        >
          <div className="flex items-start justify-between gap-1 sm:gap-2">
            {STAGES.map((s, i) => {
              const isActive = i === active;
              return (
                <React.Fragment key={s.id}>
                  <button
                    role="tab"
                    id={`stage-tab-${s.id}`}
                    aria-selected={isActive}
                    aria-controls={`stage-panel-${s.id}`}
                    onClick={() => pick(i)}
                    className="group flex w-16 flex-shrink-0 flex-col items-center gap-2 sm:w-28 sm:gap-3 lg:w-32"
                  >
                    <span className="relative">
                      <span
                        className={`flex h-11 w-11 items-center justify-center rounded-xl ring-1 transition-all duration-300 sm:h-14 sm:w-14 sm:rounded-2xl ${
                          isActive
                            ? `${s.railOn} shadow-lg`
                            : `${s.railOff} ring-border group-hover:ring-border-strong`
                        }`}
                      >
                        <s.icon className="h-6 w-6" aria-hidden="true" />
                      </span>
                      <span
                        className={`absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold ring-2 ring-surface transition-colors ${
                          isActive
                            ? 'bg-content text-surface'
                            : 'bg-surface-sunk text-content-subtle'
                        }`}
                      >
                        {i + 1}
                      </span>
                    </span>
                    <span
                      className={`text-xs font-semibold transition-colors sm:text-sm ${
                        isActive ? 'text-content' : 'text-content-muted group-hover:text-content'
                      }`}
                    >
                      {s.title}
                    </span>
                  </button>

                  {i < STAGES.length - 1 && (
                    <span
                      aria-hidden="true"
                      className="mt-5 hidden h-px flex-1 min-w-[0.75rem] bg-border sm:mt-7 sm:block"
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Stage panel                                                       */}
        {/* ---------------------------------------------------------------- */}
        <div
          role="tabpanel"
          id={`stage-panel-${stage.id}`}
          aria-labelledby={`stage-tab-${stage.id}`}
          className="relative overflow-hidden rounded-3xl border border-border bg-surface-sunk"
        >
          {/* Soft wash in the stage's own colour, so switching stages reads as
              a change of place rather than a change of text. */}
          <div
            className={`pointer-events-none absolute inset-x-0 top-0 h-64 bg-gradient-to-b ${stage.glow} to-transparent transition-colors duration-500`}
          />

          <div className="relative px-6 py-10 sm:px-10 sm:py-12">
            {/* -- Orbit, desktop only -------------------------------------- */}
            {/* Below lg the chips would overlap their own labels, so small
                screens get the same content as an honest flat list. */}
            {stage.shot ? (
              <div className="mx-auto max-w-4xl overflow-hidden rounded-xl border border-border bg-surface shadow-e2">
                <div className="flex items-center gap-1.5 border-b border-border bg-surface-sunk px-3 py-2" aria-hidden="true">
                  <span className="h-2.5 w-2.5 rounded-full bg-border-strong" /><span className="h-2.5 w-2.5 rounded-full bg-border-strong" /><span className="h-2.5 w-2.5 rounded-full bg-border-strong" />
                </div>
                <img src={stage.shot.src} alt={stage.shot.alt} width={1600} height={1025} loading="lazy" decoding="async" className="block w-full h-auto" />
              </div>
            ) : (<>
            <div className="hidden lg:flex lg:justify-center">
              <div
                className="relative"
                style={{ width: ORBIT_W, height: ORBIT_H }}
                aria-hidden="true"
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={stage.id}
                    className="absolute inset-0"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {/* Centre tile */}
                    <motion.div
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                      initial={reduceMotion ? false : { scale: 0.7, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                    >
                      <div
                        className={`flex h-24 w-24 items-center justify-center rounded-3xl ${stage.tileBg} shadow-lg ring-1 ${stage.ring}`}
                      >
                        <stage.icon
                          className={`h-10 w-10 ${stage.tileFg}`}
                          aria-hidden="true"
                        />
                      </div>
                    </motion.div>

                    {/* Chips: launched from behind the tile, settling into orbit */}
                    {stage.chips.map((chip, i) => {
                      const slot = slots[i];
                      return (
                        <motion.div
                          key={chip.label}
                          className="absolute"
                          style={{ left: slot.left, top: slot.top }}
                          initial={
                            reduceMotion
                              ? { opacity: 0, x: '-50%', y: '-50%' }
                              : {
                                  opacity: 0,
                                  scale: 0.4,
                                  x: `calc(-50% + ${slot.dx}px)`,
                                  y: `calc(-50% + ${slot.dy}px)`,
                                }
                          }
                          animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
                          transition={
                            reduceMotion
                              ? { duration: 0.2, delay: 0.03 * i }
                              : {
                                  type: 'spring',
                                  stiffness: 190,
                                  damping: 20,
                                  delay: 0.12 + i * 0.07,
                                }
                          }
                        >
                          <ChipPill chip={chip} stage={stage} />
                        </motion.div>
                      );
                    })}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* -- Mobile / tablet: tile above, chips spilling out below ----- */}
            {/* An orbit at phone width puts chips on top of their own labels,
                so the ring becomes a wrapped list. The motion still reads the
                same way though: the chips come out of the tile and settle,
                one after another. Losing the animation entirely on the screen
                most visitors arrive on was the wrong trade. */}
            <div className="lg:hidden">
              <AnimatePresence mode="wait">
                <motion.div key={stage.id}>
                  <motion.div
                    className={`mx-auto flex h-20 w-20 items-center justify-center rounded-3xl ${stage.tileBg} shadow-md ring-1 ${stage.ring}`}
                    initial={reduceMotion ? { opacity: 0 } : { scale: 0.7, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                  >
                    <stage.icon className={`h-9 w-9 ${stage.tileFg}`} aria-hidden="true" />
                  </motion.div>

                  <div className="mt-6 flex flex-wrap justify-center gap-2.5">
                    {stage.chips.map((chip, i) => (
                      <motion.div
                        key={chip.label}
                        initial={
                          reduceMotion
                            ? { opacity: 0 }
                            : { opacity: 0, scale: 0.5, y: -28 }
                        }
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={
                          reduceMotion
                            ? { duration: 0.2, delay: 0.03 * i }
                            : {
                                type: 'spring',
                                stiffness: 190,
                                damping: 20,
                                delay: 0.12 + i * 0.07,
                              }
                        }
                      >
                        <ChipPill chip={chip} stage={stage} />
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
            </>)}

            {/* -- Copy ----------------------------------------------------- */}
            <AnimatePresence mode="wait">
              <motion.div
                key={stage.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
                className="mx-auto mt-10 max-w-2xl text-center"
              >
                <h3 className="mt-2 text-title text-balance text-content sm:text-title-lg">
                  {stage.headline}
                </h3>
                <p className="mt-4 text-body leading-relaxed text-content-muted">
                  {stage.text}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* -- Prev / Next ---------------------------------------------- */}
            <div className="mt-10 flex items-center justify-center gap-3">
              <button
                onClick={() => pick(active - 1)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium text-content-muted transition-colors hover:text-content hover:border-border-strong"
              >
                <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
                Prev
              </button>

              <div className="flex items-center gap-2 px-2" aria-hidden="true">
                {STAGES.map((s, i) => (
                  <button
                    key={s.id}
                    onClick={() => pick(i)}
                    aria-label={`Go to ${s.title}`}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === active ? 'w-6 bg-content' : 'w-1.5 bg-border-strong'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={() => pick(active + 1)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium text-content-muted transition-colors hover:text-content hover:border-border-strong"
              >
                Next
                <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>

        <p className="mx-auto mt-12 max-w-prose text-center text-body text-content-muted">
          All five stages run against the same solicitation and the same company profile, so
          nothing has to be re-entered on the way from finding a bid to sending one.
        </p>
      </div>
    </div>
  );
};

export default HowItWorks;
